import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { MultiGroupFilter } from "components/Filters/MultiGroupFilter";
import { DownloadIcon } from "components/icons/Download";
import { defaultSortOption, sortOptions } from "constants/foreignCATask";
import {
  NO_SERVICE_TEAM_NAME,
  NO_SERVICE_TEAM_UUID,
} from "constants/settingServiceProvider";
import { COMPANY_GROUP_TAG } from "constants/tagCategory";
import { NO_TAG, NO_TAG_KEY } from "constants/tagColors";
import {
  ACCOUNTANT_ACCESS_OPTIONS,
  BANK_CONNECTION_OPTIONS,
  BOOKKEEPING_SERVICE_TYPES,
  SERVICE_FILTER_OPTIONS,
} from "constants/task";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import {
  useGetBooksGlobalStatusOptionsQuery,
  useLazyGetAllBooksEntityQuery,
} from "store/apis/bookkeepingTracker";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { useGetAllInkleCPATeamsQuery } from "store/apis/internalTeamSetting";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { Tag } from "types/Models/FCA";
import { BackendError } from "types/utils/error";
import { validate } from "uuid";
import {
  UpdateFilterFunction,
  UpdateMultipleFilterFunction,
} from "hooks/useFilter";
import { BookkeepingTrackerFilterValues } from "./BookkeepingTracker";

type BookkeepingTrackerFilterType = {
  selectedTag: string | null;
  selectedCompanyTag: string | null;
  tags: Tag[];
  selectedServicesFilter: string | null;
  taskStates: { name: string; uuid: string }[];
  selectedSeason: string | null;
  selectedSort: string | null;
  showSortFeature: boolean;
  selectedTeams: string | null;
  selectedMembers: string | null;
  serviceTeamId?: string;
  showUnassignedTeam: string | null;
  showUnassignedBookkeeper: string | null;
  selectedAccountAccess: string | null;
  selectedBankConnection: string | null;
  selectedFormationType: string | null;
  pendingActionsView: string | null;
  filters: string;
  companyGroups?: string | null;
  selectedSpocIds?: string | null;
  selectedBooksGlobalStatus?: string | null;
  updateFilter: UpdateFilterFunction<BookkeepingTrackerFilterValues>;
  updateMultipleFilters: UpdateMultipleFilterFunction<BookkeepingTrackerFilterValues>;
};

export const BookkeepingTrackerFilter = ({
  selectedTag,
  selectedCompanyTag,
  tags,
  selectedServicesFilter,
  taskStates,
  selectedSeason,
  selectedSort,
  selectedTeams,
  selectedMembers,
  serviceTeamId,
  showUnassignedTeam,
  showUnassignedBookkeeper,
  selectedBankConnection,
  selectedAccountAccess,
  selectedFormationType,
  pendingActionsView,
  filters,
  companyGroups,
  selectedSpocIds,
  selectedBooksGlobalStatus,
  updateFilter,
  updateMultipleFilters,
}: BookkeepingTrackerFilterType) => {
  const [hideSortDiv, setHideSortDiv] = useState(true);
  const { isAdmin } = useRoleBasedView();
  const { data: companyTagsData = [] } = useGetTagsForCategoryQuery({
    tagType: COMPANY_GROUP_TAG,
  });
  const { data: teamOptions = [] } = useGetAllInkleCPATeamsQuery(
    {
      accessibleTeams: "CPA_TEAM",
    },
    { skip: !isAdmin }
  );
  const serviceTeamOptions = [
    ...teamOptions,
    { uuid: NO_SERVICE_TEAM_UUID, name: NO_SERVICE_TEAM_NAME },
  ];

  const serviceTeamIds = (selectedTeams || serviceTeamId)
    ?.split(",")
    .filter((id) => validate(id))
    .join(",");

  const { data: teamMembers = [] } = useGetServiceTeamQuery(
    {
      serviceTeamId: serviceTeamIds,
    },
    { skip: !serviceTeamIds, refetchOnMountOrArgChange: true }
  );
  const companyTags = [...companyTagsData, { title: NO_TAG, uuid: NO_TAG_KEY }];

  const { isLoading: isCountiesLoading, data } = useGetCountriesDataQuery();
  const US_FORMATION_TYPE =
    data?.find(
      ({ name, code_alpha_2 }) =>
        code_alpha_2 === "US" && name === "United States"
    )?.formation_type || [];

  const seasonFilter = (values: string[] | null) => {
    updateFilter("season_filter_uuids", values?.join(",") || null);
  };

  const groupFilter = (values: string[] | null) => {
    updateFilter("company_groups", values?.join(",") || null);
  };

  const serviceTeamFilter = (values: string[] | null) => {
    updateFilter("service_team_uuids", values?.join(",") || null);
  };

  const teamMemberFilter = (values: string[] | null) => {
    updateFilter("team_member_uuids", values?.join(",") || null);
  };

  const updateTagFilter = (values: string[] | null) => {
    updateFilter("task_tag_filter_uuids", values?.join(",") || null);
  };

  const updateCompanyTagFilter = (values: string[] | null) => {
    updateFilter("company_tag_filter_uuids", values?.join(",") || null);
  };

  const servicesFilter = (values: string[] | null) => {
    updateFilter("service_task_filter", values?.join(",") || null);
  };

  const spocFilter = (values: string[] | null) => {
    updateFilter("spoc_ids", values?.join(",") || null);
  };

  const booksGlobalStatusFilter = (values: string[] | null) => {
    updateFilter("books_global_status", values?.join(",") || null);
  };

  const {
    data: booksGlobalStatusOptions = [],
    isLoading: isBooksGlobalStatusOptionsLoading,
    isSuccess: isBooksGlobalStatusOptionsSuccess,
  } = useGetBooksGlobalStatusOptionsQuery(undefined, {
    skip: Boolean(pendingActionsView),
  });

  const { data: spocList = [] } = useGetAssignAgentsQuery(
    { agentType: "CALL_EXECUTIVE" },
    { skip: !pendingActionsView || !isAdmin }
  );

  const isMonthlyBooksActive =
    selectedServicesFilter
      ?.split(",")
      .includes(BOOKKEEPING_SERVICE_TYPES.MONTHLY_BOOKKEEPING) ?? false;

  const toggleMonthlyBooksFilter = () => {
    if (isMonthlyBooksActive) {
      const remaining = selectedServicesFilter
        ?.split(",")
        .filter((s) => s !== BOOKKEEPING_SERVICE_TYPES.MONTHLY_BOOKKEEPING);
      servicesFilter(remaining?.length ? remaining : null);
    } else {
      const existing = selectedServicesFilter?.split(",").filter(Boolean) || [];
      servicesFilter([
        ...existing,
        BOOKKEEPING_SERVICE_TYPES.MONTHLY_BOOKKEEPING,
      ]);
    }
  };

  const updateBankConnectionFilter = (value: string | null) => {
    updateFilter("bank_connections", value || null);
  };

  const updateAccountantAccessFilter = (value: string | null) => {
    updateFilter("accountant_access", value || null);
  };

  const updateNoTeamFilter = () => {
    updateFilter("show_unassigned_team", showUnassignedTeam ? null : "true");
  };

  const updateNoAssigneeFilter = () => {
    updateFilter(
      "show_unassigned_bookkeeper",
      showUnassignedBookkeeper ? null : "true"
    );
  };

  const updateFormationTypeFilter = (values: string[] | null) => {
    updateFilter("formation_type_filter_uuids", values?.join(",") || null);
  };

  const updateSortFilter = (
    prop: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    updateFilter(
      "task_sort_by",
      (prop as SingleValue<OptionData>)?.value || null
    );
  };

  const selectedSortOption = sortOptions.find(
    (option) => option.sortKey === selectedSort
  );

  const { successToast, alertToast } = useToast();
  const [getAllBooksEntity, { isFetching }] = useLazyGetAllBooksEntityQuery();

  const downloadCSV = async (exportToMail = true) => {
    try {
      const res = await getAllBooksEntity({
        ...JSON.parse(filters),
        generate_report: true,
        export_to_email: exportToMail,
      }).unwrap();

      if (res?.download_url) {
        window.open(res?.download_url);
      }

      successToast({
        message: exportToMail
          ? "Bookkeeping report has been sent to your email"
          : "Bookkeeping report downloaded successfully",
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const onViewPendingAction = (checked: boolean) => {
    updateMultipleFilters({
      pending_actions_view: checked ? "true" : null,
      page: 1,
      search_term: null,
    });
  };

  return (
    <div className="t-flex t-items-baseline t-justify-between t-gap-12">
      <div className="t-flex t-items-center t-gap-2">
        <Filter.Root
          key={`filter-${pendingActionsView}`}
          defaultValue={!pendingActionsView ? "SERVICES" : "GROUPS"}
          title="Filter"
          capsule={
            <>
              {isAdmin && (
                <div className="t-flex t-gap-1 t-items-center t-rounded t-border t-border-solid t-border-neutral-10 t-px-3 t-py-1.5 t-text-body-sm">
                  <label
                    htmlFor="view-pending-action"
                    className="t-cursor-pointer t-text-text-60"
                  >
                    View pending support actions
                  </label>
                  <Switch
                    id="view-pending-action"
                    size="small"
                    checked={Boolean(pendingActionsView)}
                    onCheckedChange={onViewPendingAction}
                  />
                </div>
              )}
              {!pendingActionsView && (
                <>
                  {isAdmin && (
                    <Chip
                      onRemove={updateNoAssigneeFilter}
                      isActive={Boolean(showUnassignedBookkeeper)}
                      isFixedFilter
                      onClick={updateNoAssigneeFilter}
                    >
                      Unassigned Bookkeeper
                    </Chip>
                  )}
                  <Chip
                    onRemove={toggleMonthlyBooksFilter}
                    isActive={isMonthlyBooksActive}
                    isFixedFilter
                    onClick={toggleMonthlyBooksFilter}
                  >
                    Monthly Books Clients
                  </Chip>
                </>
              )}
              {selectedFormationType &&
                selectedFormationType.split(",").length > 0 && (
                  <Chip
                    onRemove={() => updateFormationTypeFilter(null)}
                    filterType="FORMATION_TYPE"
                    isActive
                  >
                    Formation Type ({selectedFormationType.split(",").length})
                  </Chip>
                )}
              {selectedServicesFilter &&
                selectedServicesFilter?.split(",").length > 0 && (
                  <Chip
                    onRemove={() => servicesFilter(null)}
                    isActive
                    filterType="SERVICES"
                  >
                    Services ({selectedServicesFilter.split(",").length})
                  </Chip>
                )}
              {selectedBooksGlobalStatus &&
                selectedBooksGlobalStatus.split(",").length > 0 && (
                  <Chip
                    onRemove={() => booksGlobalStatusFilter(null)}
                    isActive
                    filterType="BOOKS_GLOBAL_STATUS"
                  >
                    Books Status ({selectedBooksGlobalStatus.split(",").length})
                  </Chip>
                )}
              {selectedSpocIds && selectedSpocIds.split(",").length > 0 && (
                <Chip
                  onRemove={() => spocFilter(null)}
                  isActive
                  filterType="SPOC"
                >
                  SPOC ({selectedSpocIds.split(",").length})
                </Chip>
              )}
              {companyGroups && companyGroups.split(",").length > 0 && (
                <Chip
                  onRemove={() => groupFilter(null)}
                  isActive
                  filterType="GROUPS"
                >
                  Company ({companyGroups.split(",").length})
                </Chip>
              )}
              {selectedSeason && selectedSeason?.split(",").length > 0 && (
                <Chip
                  onRemove={() => seasonFilter(null)}
                  isActive
                  filterType="SEASON"
                >
                  Season ({selectedSeason.split(",").length})
                </Chip>
              )}
              {selectedTag && selectedTag?.split(",").length > 0 && (
                <Chip
                  onRemove={() => updateTagFilter(null)}
                  isActive
                  filterType="TAG"
                >
                  Task Tag ({selectedTag.split(",").length})
                </Chip>
              )}
              {selectedCompanyTag &&
                selectedCompanyTag?.split(",").length > 0 && (
                  <Chip
                    onRemove={() => updateCompanyTagFilter(null)}
                    isActive
                    filterType="COMPANY_TAG"
                  >
                    Company Tag ({selectedCompanyTag.split(",").length})
                  </Chip>
                )}
              {selectedTeams && selectedTeams?.split(",").length > 0 && (
                <Chip
                  onRemove={() => serviceTeamFilter([])}
                  isActive
                  filterType="SERVICE_TEAM"
                >
                  Service Team ({selectedTeams?.split(",").length})
                </Chip>
              )}
              {selectedMembers && selectedMembers?.split(",").length > 0 && (
                <Chip
                  onRemove={() => teamMemberFilter([])}
                  isActive
                  filterType="SERVICE_TEAM_MEMBER"
                >
                  Bookkeeper ({selectedMembers?.split(",").length})
                </Chip>
              )}
              {selectedBankConnection && (
                <Chip
                  onRemove={() => updateBankConnectionFilter(null)}
                  isActive
                  filterType="BANK_CONNECTION"
                >
                  Bank Connection
                </Chip>
              )}

              {selectedAccountAccess && (
                <Chip
                  onRemove={() => updateAccountantAccessFilter(null)}
                  isActive
                  filterType="ACCOUNTANT_ACCESS"
                >
                  Accountant access
                </Chip>
              )}
            </>
          }
        >
          <Filter.Portal>
            <Filter.List>
              {pendingActionsView && (
                <>
                  <Filter.ListItem value="GROUPS">
                    Company groups
                  </Filter.ListItem>
                  <Filter.ListItem value="SERVICES">Services</Filter.ListItem>
                  <Filter.ListItem value="SPOC">SPOC</Filter.ListItem>
                </>
              )}
              {!pendingActionsView && (
                <>
                  <Filter.ListItem value="SERVICES">Services</Filter.ListItem>
                  <Filter.ListItem value="SEASON">Season</Filter.ListItem>
                  <Filter.ListItem value="BOOKS_GLOBAL_STATUS">
                    Books Status
                  </Filter.ListItem>
                  <Filter.ListItem value="TAG">Tag</Filter.ListItem>
                  <Filter.ListItem value="COMPANY_TAG">
                    Company Tag
                  </Filter.ListItem>
                  <Filter.ListItem value="SERVICE_TEAM">
                    Service Team
                  </Filter.ListItem>
                  {serviceTeamIds && (
                    <Filter.ListItem value="SERVICE_TEAM_MEMBER">
                      Bookkeeper
                    </Filter.ListItem>
                  )}
                  <Filter.ListItem value="FORMATION_TYPE">
                    Formation Type
                  </Filter.ListItem>
                  <Filter.ListItem value="BANK_CONNECTION">
                    Bank connections
                  </Filter.ListItem>
                  <Filter.ListItem value="ACCOUNTANT_ACCESS">
                    Accountant access
                  </Filter.ListItem>
                </>
              )}
            </Filter.List>
            <Filter.Body value="GROUPS" block>
              {taskStates && (
                <MultiGroupFilter
                  updateFilter={(_, values) => groupFilter(values)}
                  values={{
                    GROUPS: companyGroups?.split(",") || [],
                  }}
                />
              )}
            </Filter.Body>
            <Filter.Body value="SERVICES" block>
              {taskStates && (
                <MultiSelectFilter
                  onChange={servicesFilter}
                  options={SERVICE_FILTER_OPTIONS}
                  selected={selectedServicesFilter?.split(",")}
                />
              )}
            </Filter.Body>
            <Filter.Body value="SPOC" block>
              <MultiSelectFilter
                onChange={spocFilter}
                options={spocList.map(({ profile_id, name }) => ({
                  value: profile_id,
                  label: name,
                }))}
                selected={selectedSpocIds?.split(",") || []}
              />
            </Filter.Body>
            <Filter.Body value="SEASON" block>
              <MultiSelectFilter
                onChange={seasonFilter}
                options={[
                  {
                    label: "2024",
                    value: "2024",
                  },
                  {
                    label: "2025",
                    value: "2025",
                  },
                ]}
                selected={selectedSeason?.split(",")}
              />
            </Filter.Body>
            <Filter.Body value="BOOKS_GLOBAL_STATUS" block>
              <Async.Root
                isLoading={isBooksGlobalStatusOptionsLoading}
                isSuccess={isBooksGlobalStatusOptionsSuccess}
                isEmpty={false}
                customLoader={<Loader size="small" />}
              >
                <Async.Success>
                  <MultiSelectFilter
                    isSearchable={false}
                    onChange={booksGlobalStatusFilter}
                    options={booksGlobalStatusOptions.map(
                      ({ display, value }) => ({ value, label: display })
                    )}
                    selected={selectedBooksGlobalStatus?.split(",") || []}
                  />
                </Async.Success>
              </Async.Root>
            </Filter.Body>
            <Filter.Body value="TAG" block>
              {tags && (
                <MultiSelectFilter
                  onChange={updateTagFilter}
                  options={tags.map((tag) => ({
                    value: tag.uuid,
                    label: tag.title,
                  }))}
                  selected={selectedTag?.split(",")}
                />
              )}
            </Filter.Body>
            <Filter.Body value="COMPANY_TAG" block>
              {companyTags && (
                <MultiSelectFilter
                  onChange={updateCompanyTagFilter}
                  options={companyTags.map((tag) => ({
                    value: tag.uuid,
                    label: tag.title,
                  }))}
                  selected={selectedCompanyTag?.split(",")}
                />
              )}
            </Filter.Body>
            <Filter.Body value="SERVICE_TEAM" block>
              <MultiSelectFilter
                onChange={serviceTeamFilter}
                options={serviceTeamOptions?.map((team) => ({
                  value: team.uuid,
                  label: team.name,
                }))}
                selected={selectedTeams?.split(",")}
              />
            </Filter.Body>
            <Filter.Body value="SERVICE_TEAM_MEMBER" block>
              <MultiSelectFilter
                onChange={teamMemberFilter}
                options={teamMembers?.map((member) => ({
                  value: member.uuid,
                  label: member.name,
                }))}
                selected={selectedMembers?.split(",")}
              />
            </Filter.Body>
            <Filter.Body value="FORMATION_TYPE" block>
              {isCountiesLoading ? (
                <Loader size="small" />
              ) : (
                <MultiSelectFilter
                  isSearchable={false}
                  onChange={updateFormationTypeFilter}
                  options={US_FORMATION_TYPE.map(({ name, uuid }) => ({
                    value: uuid,
                    label: name,
                  }))}
                  selected={selectedFormationType?.split(",") || []}
                />
              )}
            </Filter.Body>
            <Filter.Body value="BANK_CONNECTION" block>
              <Radio.Root onValueChange={updateBankConnectionFilter}>
                <Radio.Content>
                  {BANK_CONNECTION_OPTIONS?.map((bank) => (
                    <Radio.Item
                      asChild
                      value={bank.value}
                      key={bank.value}
                      checked={selectedBankConnection === bank.value}
                    >
                      {bank.label}
                    </Radio.Item>
                  ))}
                </Radio.Content>
              </Radio.Root>
            </Filter.Body>
            <Filter.Body value="ACCOUNTANT_ACCESS" block>
              <Radio.Root onValueChange={updateAccountantAccessFilter}>
                <Radio.Content>
                  {ACCOUNTANT_ACCESS_OPTIONS?.map((access) => (
                    <Radio.Item
                      asChild
                      value={access.value}
                      key={access.value}
                      checked={selectedAccountAccess === access.value}
                    >
                      {access.label}
                    </Radio.Item>
                  ))}
                </Radio.Content>
              </Radio.Root>
            </Filter.Body>
          </Filter.Portal>
        </Filter.Root>
      </div>

      {!pendingActionsView && (
        <div className="t-flex t-items-center t-gap-4">
          <Dropdown.Root>
            <Dropdown.Trigger asChild className="t-group">
              <div>
                <Button
                  size="small"
                  customType="icon"
                  onClick={(e) => e.stopPropagation()}
                  isLoading={isFetching}
                  disabled={isFetching}
                >
                  <div className="t-flex t-items-center t-gap-1.5">
                    <DownloadIcon color="#706A85" size={20} />
                  </div>
                </Button>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content sideOffset={4} align="end" side="bottom">
                <ExportOptions
                  onLocalDownload={() => downloadCSV(false)}
                  onMailExport={() => downloadCSV(true)}
                />
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
          <div className="t-min-w-60">
            <Combobox
              fullWidth
              size="small"
              menuPlacement="auto"
              creatable={false}
              withForm={false}
              components={{
                ClearIndicator: () => null,
              }}
              onChange={updateSortFilter}
              options={sortOptions.map(({ id, title, sortKey }) => ({
                label: title,
                value: sortKey,
              }))}
              defaultValue={
                selectedSortOption
                  ? {
                      label: selectedSortOption.title,
                      value: selectedSortOption.sortKey,
                    }
                  : {
                      label: defaultSortOption.title,
                      value: "",
                    }
              }
              menuPortalTarget={document.body}
            />
          </div>
        </div>
      )}
    </div>
  );
};
